/**
 * Truncates a string by showing the first `top` characters, ellipses, and the last `tail` characters.
 * If the string is shorter than top + tail + 3 (for ellipses), returns the original string.
 * 
 * @param str - The input string to truncate
 * @param top - Number of characters to show at the beginning
 * @param tail - Number of characters to show at the end
 * @returns The truncated string with ellipses in the middle
 * 
 * @example
 * topAndTail("did:web:example.com:very:long:identifier:path", 16, 24)
 * // Returns: "did:web:...her:path"
 */
export function topAndTail(str: string, top: number, tail: number): string {
  // If string is shorter than what we'd show with ellipses, return original
  if (str.length <= top + tail + 3) {
    return str;
  }
  
  const beginning = str.substring(0, top);
  const ending = str.substring(str.length - tail);
  
  return `${beginning}...${ending}`;
}

/**
 * Formats a date object into a consistent string format.
 * 
 * @param date - The date to format (Date object or string that can be parsed as a date)
 * @param includeTime - Whether to include the time in the output
 * @returns Formatted date string
 * 
 * @example
 * formatDateTime(new Date('2025-12-19T10:50:00'), true)
 * // Returns: "December 19, 2025 at 10:50"
 * 
 * formatDateTime(new Date('2025-12-19T10:50:00'), false)
 * // Returns: "December 19, 2025"
 */
export function formatDateTime(date: Date | string, includeTime: boolean = false): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  // Check if date is valid
  if (isNaN(dateObj.getTime())) {
    return 'Invalid date';
  }
  
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };
  
  if (includeTime) {
    options.hour = '2-digit';
    options.minute = '2-digit';
    options.hour12 = false;
  }
  
  const formatted = dateObj.toLocaleString('en-US', options);
  
  // toLocaleString with those options returns "Month Day, Year, HH:MM" when time is included
  // We want "Month Day, Year at HH:MM" format
  if (includeTime) {
    return formatted.replace(/,([^,]+)$/, ' at$1');
  }
  
  return formatted;
}

/**
 * Formats a date object to show only the time in 24-hour format.
 * Useful for chart labels and compact displays.
 * Always displays in the user's local timezone.
 * 
 * @param date - The date to format (Date object or string that can be parsed as a date)
 * @returns Formatted time string in HH:MM format
 * 
 * @example
 * formatTime(new Date('2025-12-19T10:50:00'))
 * // Returns: "10:50"
 */
export function formatTime(date: Date | string): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  // Check if date is valid
  if (isNaN(dateObj.getTime())) {
    return 'Invalid time';
  }
  
  // Use undefined locale to use browser's default locale/timezone
  const options: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone, // Explicit local timezone
  };
  
  return dateObj.toLocaleTimeString(undefined, options);
}

/**
 * Returns a human-readable "time ago" string for a given date.
 * 
 * @param date - The date to compare against now (Date object or string that can be parsed as a date)
 * @param numericDates - Whether to use numeric dates (e.g., "1 day ago") or text (e.g., "Yesterday"). Defaults to true.
 * @returns A human-readable string representing how long ago the date was
 * 
 * @example
 * timeAgo(new Date(Date.now() - 1000 * 60 * 5))
 * // Returns: "5 minutes ago"
 * 
 * timeAgo(new Date(Date.now() - 1000 * 60 * 60 * 24), false)
 * // Returns: "Yesterday"
 * 
 * timeAgo(new Date(Date.now() - 1000 * 60 * 60 * 24), true)
 * // Returns: "1 day ago"
 */
export function timeAgo(date: Date | string, numericDates: boolean = true): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  // Check if date is valid
  if (isNaN(dateObj.getTime())) {
    return 'Invalid date';
  }
  
  const now = new Date();
  const diffMs = now.getTime() - dateObj.getTime();
  
  // Calculate time units
  const seconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);
  
  if (weeks >= 1) {
    return numericDates ? '1 week ago' : 'Last week';
  } else if (days >= 2) {
    return `${days} days ago`;
  } else if (days >= 1) {
    return numericDates ? '1 day ago' : 'Yesterday';
  } else if (hours >= 2) {
    return `${hours} hours ago`;
  } else if (hours >= 1) {
    return numericDates ? '1 hour ago' : 'An hour ago';
  } else if (minutes >= 2) {
    return `${minutes} minutes ago`;
  } else if (minutes >= 1) {
    return numericDates ? '1 minute ago' : 'A minute ago';
  } else if (seconds >= 3) {
    return `${seconds} seconds ago`;
  } else {
    return 'Just now';
  }
}

/**
 * Word list for generating random paths
 */
const wordList = [
  'acrobat', 'albino', 'album', 'alcohol', 'alpha', 'analog', 'animal', 'antenna', 'apollo', 'april',
  'aroma', 'artist', 'aspirin', 'athlete', 'atlas', 'banana', 'bandit', 'banjo', 'bikini', 'bingo',
  'bonus', 'camera', 'anada', 'carbon', 'casino', 'catalog', 'cinema', 'citizen', 'cobra', 'comet',
  'compact', 'complex', 'context', 'credit', 'critic', 'crystal', 'culture', 'delta', 'dialog', 'diploma',
  'doctor', 'domino', 'dragon', 'drama', 'extra', 'fabric', 'final', 'focus', 'forum', 'galaxy',
  'gallery', 'global', 'harmony', 'hotel', 'humor', 'index', 'kilo', 'lemon', 'liter', 'lotus',
  'mango', 'melon', 'menu', 'meter', 'metro', 'mineral', 'model', 'music', 'object', 'piano',
  'pirate', 'plastic', 'radio', 'report', 'signal', 'sport', 'studio', 'subject', 'super', 'tango',
  'taxi', 'tempo', 'tennis', 'textile', 'total', 'tourist', 'video', 'visa', 'academy', 'atomic',
  'bazaar', 'brother', 'budget', 'cabaret', 'cadet', 'candle', 'capsule', 'caviar', 'channel', 'chapter',
  'circle', 'cobalt', 'comrade', 'condor', 'crimson', 'cyclone', 'declare', 'desert', 'divide', 'domain',
  'double', 'eagle', 'echo', 'eclipse', 'editor', 'educate', 'effect', 'electra', 'emerald', 'emotion',
  'empire', 'eternal', 'evening', 'exhibit', 'expand', 'explore', 'extreme', 'forget', 'freedom', 'gravity',
  'habitat', 'helium', 'holiday', 'hunter', 'iceberg', 'imagine', 'infant', 'isotope', 'kitchen', 'letter',
  'license', 'lithium', 'loyal', 'lucky', 'magenta', 'manual', 'marble', 'mayor', 'monarch', 'money',
  'morning', 'mother', 'mystery', 'native', 'nectar', 'network', 'nobody', 'nominal', 'nothing', 'number',
  'office', 'opinion', 'option', 'order', 'outside', 'package', 'pattern', 'pencil', 'people', 'phantom',
  'pioneer', 'podium', 'portal', 'potato', 'process', 'proxy', 'pupil', 'python', 'quality', 'quarter',
  'quiet', 'rabbit', 'radical', 'radius', 'rainbow', 'ravioli', 'respect', 'respond', 'result', 'resume',
  'river', 'salary', 'salsa', 'sample', 'savage', 'scarlet', 'sector', 'serpent', 'shampoo', 'silence',
  'simple', 'society', 'sonar', 'sonata', 'soprano', 'spider', 'sponsor', 'action', 'active', 'actor',
  'address', 'admiral', 'agenda', 'agent', 'airline', 'airport', 'alarm', 'algebra', 'alibi', 'alien',
  'almond', 'alpine', 'ammonia', 'analyze', 'anatomy', 'angel', 'annual', 'answer', 'apple', 'archive',
  'arctic', 'arena', 'armada', 'aspect', 'audio', 'august', 'avenue', 'average', 'axiom', 'bagel',
  'balance', 'ballad', 'ballet', 'bambino', 'bamboo', 'baron', 'basic', 'basket', 'battery', 'benefit',
  'bicycle', 'binary', 'biology', 'bishop', 'blitz', 'block', 'blonde', 'bonjour', 'bottle', 'boxer',
  'brandy', 'bravo', 'bridge', 'bronze', 'brown', 'burger', 'cabinet', 'cactus', 'cafe', 'camel',
  'campus', 'canal', 'cannon', 'canoe', 'cantina', 'canvas', 'canyon', 'capital', 'caramel', 'caravan',
  'career', 'cargo', 'carpet', 'cartel', 'cartoon', 'castle', 'cement', 'center', 'century', 'ceramic',
  'chamber', 'chance', 'change', 'chaos', 'charm', 'charter', 'cheese', 'chef', 'chemist', 'cherry',
  'chess', 'chicken', 'chief', 'cigar', 'circus', 'city', 'classic', 'clean', 'client', 'climax',
  'clinic', 'clock', 'club', 'cockpit', 'coconut', 'cola', 'collect', 'colony', 'combat', 'comedy',
  'command', 'company', 'concert', 'connect', 'consul', 'contact', 'contour', 'control', 'convert', 'copy',
  'corner', 'corona', 'correct', 'cosmos', 'couple', 'courage', 'cowboy', 'craft', 'crash', 'cricket',
  'crown', 'dance', 'decade', 'decimal', 'degree', 'delete', 'deliver', 'deluxe', 'demand', 'demo',
  'design', 'detect', 'develop', 'diagram', 'diamond', 'diesel', 'diet', 'digital', 'dilemma', 'direct',
  'disco', 'distant', 'dollar', 'dolphin', 'drink', 'driver', 'duet', 'dynamic', 'earth', 'east',
  'ecology', 'economy', 'elastic', 'elegant', 'element', 'elite', 'email', 'empty', 'energy', 'engine',
  'english', 'episode', 'equator', 'escape', 'escort', 'ethnic', 'evident', 'exact', 'example', 'exit',
  'exotic', 'export', 'express', 'factor', 'falcon', 'family', 'fantasy', 'fashion', 'fiber', 'fiction',
  'fiesta', 'figure', 'film', 'filter', 'finance', 'finish', 'first', 'flag', 'flash', 'flower',
  'fluid', 'flute', 'folio', 'forest', 'formal', 'formula', 'fortune', 'forward', 'fragile', 'fresh',
  'friend', 'frozen', 'future', 'gamma', 'garage', 'garden', 'garlic', 'gemini', 'general', 'genetic',
  'genius', 'gold', 'golf', 'gondola', 'gong', 'good', 'gorilla', 'grand', 'granite', 'graph',
  'green', 'group', 'guide', 'guitar', 'guru', 'hand', 'happy', 'harbor', 'hello', 'history',
  'horizon', 'house', 'human', 'icon', 'idea', 'igloo', 'image', 'impact', 'import', 'input',
  'insect', 'instant', 'iris', 'jacket', 'jaguar', 'jargon', 'jazz', 'jeep', 'joker', 'journey',
  'juice', 'jungle', 'kale', 'karma', 'keen', 'kettle', 'kiwi', 'knife', 'knot', 'lab',
  'lady', 'laser', 'launch', 'leader', 'legend', 'legal', 'leisure', 'lesson', 'level', 'liberty',
  'linear', 'local', 'logic', 'lotto', 'lounge', 'loyalty', 'lyrics', 'mail', 'major', 'mall',
  'manager', 'map', 'market', 'mask', 'metal', 'meter', 'midnight', 'miracle', 'mirror', 'mission',
  'modem', 'monitor', 'moral', 'naval', 'navigare', 'needle', 'neon', 'neutral', 'newton', 'normal',
  'oasis', 'ocean', 'office', 'old', 'opera', 'organic', 'oxygen', 'painter', 'palace', 'palma',
  'parade', 'paris', 'pastel', 'pearl', 'pedal', 'pelican', 'pepper', 'phoenix', 'pillar', 'pink',
  'planet', 'plastic', 'platform', 'player', 'plus', 'poem', 'polar', 'pope', 'port', 'pot',
  'power', 'prism', 'prince', 'printer', 'prize', 'program', 'public', 'quest', 'quota', 'race',
  'rail', 'raisin', 'rally', 'random', 'rapid', 'ratio', 'reactor', 'reader', 'relief', 'rhythm',
  'rigid', 'river', 'rocket', 'rose', 'route', 'royal', 'safari', 'sample', 'sapphire', 'scheme',
  'school', 'science', 'scout', 'second', 'secret', 'segment', 'series', 'shadow', 'share', 'shelter',
  'shift', 'signal', 'silver', 'simple', 'singer', 'sister', 'skate', 'sky', 'smart', 'smoke',
  'soap', 'soda', 'soft', 'solar', 'solid', 'sonic', 'soul', 'source', 'south', 'sovereign',
  'spare', 'spark', 'speed', 'spider', 'spirit', 'sponge', 'square', 'star', 'station', 'steel',
  'stone', 'storm', 'strong', 'studio', 'style', 'sugar', 'summit', 'summer', 'sunday', 'sunset',
  'superior', 'support', 'survey', 'symbol', 'table', 'target', 'team', 'tempo', 'tennis', 'text',
  'think', 'tiger', 'tilt', 'timber', 'titan', 'tonic', 'total', 'tower', 'toy', 'track',
  'trade', 'traffic', 'train', 'trick', 'trip', 'trooper', 'trust', 'turbo', 'turn', 'ultra',
  'union', 'unique', 'unity', 'urban', 'vanilla', 'vegan', 'vehicle', 'version', 'vessel', 'video',
  'vision', 'vital', 'vivid', 'voice', 'volcano', 'voltage', 'volume', 'voyage', 'wagon', 'walk',
  'wall', 'warrior', 'waste', 'watch', 'water', 'weather', 'wedding', 'week', 'weight', 'west',
  'wheel', 'whiskey', 'white', 'window', 'wisdom', 'wish', 'world', 'writer', 'xylophone', 'yellow',
  'zebra', 'zero', 'zodiac', 'zoo',
];

/**
 * Gets a random word from the word list
 * 
 * @returns A random word from the word list
 * 
 * @example
 * getRandomWord()
 * // Returns: "dragon" (or any other word from the list)
 */
export function getRandomWord(): string {
  return wordList[Math.floor(Math.random() * wordList.length)];
}

/**
 * Generates a random path in the format /word1/word2
 * 
 * @returns A random path with two words separated by slashes
 * 
 * @example
 * generateRandomPath()
 * // Returns: "/dragon/castle" (or any other combination)
 */
export function generateRandomPath(): string {
  return `/${getRandomWord()}/${getRandomWord()}`;
}

/**
 * Converts a string to a valid secret ID in snake_case format.
 * Only allows A-Z, a-z, 0-9, underscore (_), and hyphen (-).
 * Removes or replaces all other characters.
 * 
 * @param name - The secret name to convert
 * @returns A snake_case identifier safe for use in $SECRET:xxx syntax
 * 
 * @example
 * toSecretId("My API Key")
 * // Returns: "my_api_key"
 * 
 * toSecretId("Production-Database@2024")
 * // Returns: "production_database_2024"
 * 
 * toSecretId("AWS_S3_Access_Token")
 * // Returns: "aws_s3_access_token"
 */
export function toSecretId(name: string): string {
  return name
    .toLowerCase()
    .trim()
    // Replace whitespace and common separators with underscore
    .replace(/[\s\-\.]+/g, '_')
    // Remove any characters that aren't alphanumeric, underscore, or hyphen
    .replace(/[^a-z0-9_\-]/g, '')
    // Replace multiple consecutive underscores with single underscore
    .replace(/_+/g, '_')
    // Remove leading/trailing underscores or hyphens
    .replace(/^[_\-]+|[_\-]+$/g, '');
}

/**
 * Format a log timestamp according to user preference
 * @param timestamp - Unix timestamp (seconds)
 * @param format - Display format preference
 * @returns Formatted timestamp string
 */
export function formatLogTimestamp(
  timestamp: number,
  format: 'utc' | 'local' | 'relative' | 'compact' = 'local'
): string {
  const date = new Date(timestamp * 1000);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);

  switch (format) {
    case 'utc':
      // ISO 8601 UTC format: 2025-01-07T10:30:45Z
      return date.toISOString();

    case 'local':
      // Local datetime: 01/07/2025, 10:30:45
      return date.toLocaleString(undefined, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });

    case 'relative':
      // Relative time: "2 minutes ago", "just now"
      if (diffSecs < 10) return 'just now';
      if (diffSecs < 60) return `${diffSecs}s ago`;
      if (diffSecs < 3600) return `${Math.floor(diffSecs / 60)}m ago`;
      if (diffSecs < 86400) return `${Math.floor(diffSecs / 3600)}h ago`;
      return `${Math.floor(diffSecs / 86400)}d ago`;

    case 'compact':
      // Compact time only: 10:30:45
      return date.toLocaleTimeString(undefined, {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });

    default:
      return date.toLocaleString();
  }
}
